import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check, CloudOff, Loader2, Lock, PanelRight, Maximize2, Minimize2,
  Bold, Italic, Heading1, Heading2, List, ListChecks, Quote, Code2,
  FileText, LayoutDashboard, Eye, Pencil, StickyNote as StickyIcon,
} from 'lucide-react';
import { Badge } from '@/shared/components/ui/Badge';
import { cn } from '@/shared/utils/utils';
import { accessMeta, STICKY_COLORS } from '../constants';
import { parseStickies } from '../types';
import { countWords, readingTimeMin, applyInlineWrap, applyLinePrefix, toggleTaskLine, type EditResult } from '../editorFormat';
import type { Note, SaveNotePayload, StickyColor, StickyElement } from '../types';
import { StickyNote } from './StickyNote';
import { MarkdownPreview } from './MarkdownPreview';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';
type Mode = 'document' | 'board';

interface NoteEditorProps {
  note: Note;
  canEdit: boolean;
  focusMode: boolean;
  propertiesOpen: boolean;
  onToggleFocus: () => void;
  onToggleProperties: () => void;
  /** Persist editable fields (title/body/sticky elements). Sharing fields preserved. */
  onSave: (id: string, payload: SaveNotePayload) => Promise<void>;
}

const rid = () => Math.random().toString(36).slice(2, 9);

function fmtEdited(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return '';
  const diff = Date.now() - d;
  const min = 60_000, hr = 3_600_000, day = 86_400_000;
  if (diff < min) return 'just now';
  if (diff < hr) return `${Math.floor(diff / min)}m ago`;
  if (diff < day) return `${Math.floor(diff / hr)}h ago`;
  return new Date(iso).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

/**
 * The professional notepad workspace. Two cohesive modes:
 *  • Document — a clean, centred writing sheet with a formatting toolbar
 *    (Markdown), an Edit/Preview switch, and a live word-count / reading-time
 *    status bar.
 *  • Board — free-floating colored sticky notes on a subtle dotted-grid canvas.
 * Title/body and the sticky `elements` array autosave after a short debounce;
 * sharing fields are preserved untouched so autosave never alters access.
 */
export function NoteEditor({
  note, canEdit, focusMode, propertiesOpen, onToggleFocus, onToggleProperties, onSave,
}: NoteEditorProps) {
  const [title, setTitle] = useState(note.title);
  const [text, setText] = useState(note.notebookText ?? '');
  const [stickies, setStickies] = useState<StickyElement[]>(() => parseStickies(note.elements));
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const initialStickies = parseStickies(note.elements).length > 0 && !(note.notebookText ?? '').trim();
  const [mode, setMode] = useState<Mode>(initialStickies ? 'board' : 'document');
  const [preview, setPreview] = useState(false);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedId = useRef(note.id);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const pendingSel = useRef<{ start: number; end: number } | null>(null);

  // Reload local content when the loaded note changes (switching notes, or its
  // server data updating). Adjust state during render (via previous-value
  // tracking) instead of in an effect to avoid cascading renders.
  const [prevNote, setPrevNote] = useState(note);
  if (
    prevNote.id !== note.id ||
    prevNote.title !== note.title ||
    prevNote.notebookText !== note.notebookText ||
    prevNote.elements !== note.elements
  ) {
    setPrevNote(note);
    setTitle(note.title);
    setText(note.notebookText ?? '');
    setStickies(parseStickies(note.elements));
    setSaveState('idle');
    setPreview(false);
  }

  // Track the loaded note id and cancel any pending debounced save when the
  // loaded note changes or the editor unmounts (imperative work stays in an
  // effect, not render).
  useEffect(() => {
    loadedId.current = note.id;
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [note.id, note.title, note.notebookText, note.elements]);

  // Restore the caret/selection after a toolbar edit rewrites the body.
  useEffect(() => {
    if (pendingSel.current && bodyRef.current) {
      const { start, end } = pendingSel.current;
      pendingSel.current = null;
      bodyRef.current.focus();
      bodyRef.current.setSelectionRange(start, end);
    }
  });

  const meta = accessMeta(note.access_control);
  const words = useMemo(() => countWords(text), [text]);
  const minutes = useMemo(() => readingTimeMin(text), [text]);

  const schedule = (nextTitle: string, nextText: string, nextStickies: StickyElement[]) => {
    if (!canEdit) return;
    setSaveState('saving');
    if (timer.current) clearTimeout(timer.current);
    const id = note.id;
    timer.current = setTimeout(async () => {
      try {
        await onSave(id, {
          title: nextTitle.trim() || 'Untitled Note',
          notebookText: nextText,
          elements: nextStickies,
          access_control: note.access_control,
          shared_with_users: note.shared_with_users ?? [],
          shared_with_depts: note.shared_with_depts ?? [],
        });
        if (loadedId.current === id) setSaveState('saved');
      } catch {
        if (loadedId.current === id) setSaveState('error');
      }
    }, 800);
  };

  const changeText = (next: string) => { setText(next); schedule(title, next, stickies); };
  const changeTitle = (next: string) => { setTitle(next); schedule(next, text, stickies); };

  // Apply a toolbar transform against the textarea's current selection.
  const runFormat = (fn: (t: string, s: number, e: number) => EditResult) => {
    const el = bodyRef.current;
    const s = el?.selectionStart ?? text.length;
    const e = el?.selectionEnd ?? text.length;
    const res = fn(text, s, e);
    pendingSel.current = { start: res.selStart, end: res.selEnd };
    changeText(res.text);
  };

  // Pure text transforms only (no ref access here) so the toolbar list can be
  // used in render without capturing refs; runFormat reads the textarea ref at
  // click time.
  const TOOLS = [
    { icon: Bold, label: 'Bold', fn: (t: string, s: number, e: number) => applyInlineWrap(t, s, e, '**') },
    { icon: Italic, label: 'Italic', fn: (t: string, s: number, e: number) => applyInlineWrap(t, s, e, '*') },
    { icon: Code2, label: 'Inline code', fn: (t: string, s: number, e: number) => applyInlineWrap(t, s, e, '`') },
    { icon: Heading1, label: 'Heading 1', fn: (t: string, s: number, e: number) => applyLinePrefix(t, s, e, '# ') },
    { icon: Heading2, label: 'Heading 2', fn: (t: string, s: number, e: number) => applyLinePrefix(t, s, e, '## ') },
    { icon: List, label: 'Bulleted list', fn: (t: string, s: number, e: number) => applyLinePrefix(t, s, e, '- ') },
    { icon: ListChecks, label: 'Checklist', fn: (t: string, s: number, e: number) => toggleTaskLine(t, s, e) },
    { icon: Quote, label: 'Quote', fn: (t: string, s: number, e: number) => applyLinePrefix(t, s, e, '> ') },
  ];

  // ── Sticky (board) handlers ────────────────────────────────────────────────
  const commitStickies = (next: StickyElement[]) => { setStickies(next); schedule(title, text, next); };
  const addSticky = (color: StickyColor) => {
    const offset = (stickies.length % 6) * 26;
    commitStickies([...stickies, { id: rid(), type: 'sticky', x: 60 + offset, y: 60 + offset, w: 220, h: 200, color, text: '' }]);
  };
  const updateSticky = (id: string, patch: Partial<StickyElement>) =>
    commitStickies(stickies.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const deleteSticky = (id: string) => commitStickies(stickies.filter((s) => s.id !== id));

  return (
    <section className="flex h-full flex-1 flex-col bg-surface">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 border-b border-outline px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {/* Mode switch */}
          <div className="flex items-center rounded border border-outline p-0.5">
            <ModeTab active={mode === 'document'} onClick={() => setMode('document')} icon={FileText} label="Document" />
            <ModeTab active={mode === 'board'} onClick={() => setMode('board')} icon={LayoutDashboard} label="Board" />
          </div>

          {mode === 'document' && canEdit && !preview && (
            <div className="flex items-center gap-0.5 border-l border-outline pl-2">
              {TOOLS.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => runFormat(t.fn)}
                  aria-label={t.label}
                  title={t.label}
                  className="rounded-md p-1.5 text-on-surface-variant transition-colors hover:bg-surface-variant hover:text-on-surface"
                >
                  <t.icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          )}

          {mode === 'board' && canEdit && (
            <div className="flex items-center gap-1.5 border-l border-outline pl-2">
              <span className="inline-flex items-center gap-1 text-body-sm text-on-surface-variant">
                <StickyIcon className="h-3.5 w-3.5" /> Add
              </span>
              {STICKY_COLORS.map((c) => (
                <button
                  key={c.color}
                  type="button"
                  aria-label={`Add ${c.label} sticky note`}
                  title={`Add ${c.label} sticky note`}
                  onClick={() => addSticky(c.color)}
                  className="h-4 w-4 rounded-full ring-1 ring-black/10 transition-transform hover:scale-110"
                  style={{ background: c.dot }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Badge tone={meta.tone}>{meta.label}</Badge>
          {!canEdit && (
            <span className="inline-flex items-center gap-1 text-body-sm text-on-surface-variant">
              <Lock className="h-3.5 w-3.5" /> Read-only
            </span>
          )}
          {mode === 'document' && (
            <button
              onClick={() => setPreview((p) => !p)}
              className={cn('inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-body-sm hover:bg-surface-variant',
                preview ? 'text-primary' : 'text-on-surface-variant')}
              aria-pressed={preview}
              title={preview ? 'Edit' : 'Preview'}
            >
              {preview ? <Pencil className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span className="hidden sm:inline">{preview ? 'Edit' : 'Preview'}</span>
            </button>
          )}
          <div className="flex items-center gap-1">
            <button onClick={onToggleFocus} className="rounded-md p-1.5 text-on-surface-variant hover:bg-surface-variant"
              aria-label={focusMode ? 'Exit focus mode' : 'Focus mode'} title={focusMode ? 'Exit focus mode' : 'Focus mode'}>
              {focusMode ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            {!focusMode && (
              <button onClick={onToggleProperties}
                className={cn('rounded-md p-1.5 hover:bg-surface-variant', propertiesOpen ? 'text-primary' : 'text-on-surface-variant')}
                aria-label="Toggle properties" aria-pressed={propertiesOpen} title="Properties">
                <PanelRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      {mode === 'document' ? (
        <div className="flex-1 overflow-auto bg-surface-container-low">
          <div className="mx-auto my-8 w-full max-w-[820px] rounded-card border border-outline bg-surface px-10 py-9 shadow-surface-1">
            <input
              value={title}
              disabled={!canEdit}
              onChange={(e) => changeTitle(e.target.value)}
              placeholder="Untitled Note"
              className="w-full border-0 bg-transparent text-display-sm font-semibold text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none disabled:cursor-default"
              aria-label="Note title"
            />
            <div className="mt-2 h-px w-full bg-outline/60" />
            {preview ? (
              <div className="mt-5 min-h-[50vh]"><MarkdownPreview text={text} /></div>
            ) : (
              <textarea
                ref={bodyRef}
                value={text}
                disabled={!canEdit}
                onChange={(e) => changeText(e.target.value)}
                placeholder={canEdit ? 'Start writing… use the toolbar or Markdown (**bold**, # heading, - list, - [ ] task).' : 'This note has no text content.'}
                className="mt-5 min-h-[55vh] w-full resize-none border-0 bg-transparent text-[15px] leading-8 text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none disabled:cursor-default"
                aria-label="Note body"
              />
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto bg-surface-container-low">
          <div
            ref={canvasRef}
            className="relative mx-auto my-6 min-h-[1200px] w-full max-w-[1200px] rounded-card border border-outline bg-surface shadow-surface-1"
            style={{
              backgroundImage: 'radial-gradient(rgba(0,0,0,0.10) 1px, transparent 1px)',
              backgroundSize: '22px 22px',
            }}
          >
            {stickies.map((s) => (
              <StickyNote key={s.id} element={s} canEdit={canEdit} canvasRef={canvasRef}
                onChange={(patch) => updateSticky(s.id, patch)} onDelete={() => deleteSticky(s.id)} />
            ))}
            {canEdit && stickies.length === 0 && (
              <div className="pointer-events-none absolute left-1/2 top-24 w-64 -translate-x-1/2 rounded-card border border-dashed border-outline bg-surface/70 p-4 text-center text-body-sm text-on-surface-variant">
                Click a color dot above to drop a sticky note, then drag it anywhere on the board.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status bar */}
      <div className="flex items-center justify-between gap-3 border-t border-outline bg-surface px-4 py-1.5 text-body-sm text-on-surface-variant">
        <SaveIndicator state={saveState} canEdit={canEdit} />
        <div className="flex items-center gap-3 tabular-nums">
          {mode === 'document' ? (
            <>
              <span>{words} {words === 1 ? 'word' : 'words'}</span>
              <span className="hidden sm:inline">·</span>
              <span className="hidden sm:inline">{minutes === 0 ? '—' : `${minutes} min read`}</span>
            </>
          ) : (
            <span>{stickies.length} {stickies.length === 1 ? 'note' : 'notes'}</span>
          )}
          {note.updated_at && (<><span>·</span><span>Edited {fmtEdited(note.updated_at)}</span></>)}
        </div>
      </div>
    </section>
  );
}

function ModeTab({ active, onClick, icon: Icon, label }: {
  active: boolean; onClick: () => void; icon: typeof FileText; label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn('inline-flex items-center gap-1.5 rounded-[6px] px-2.5 py-1 text-body-sm font-medium transition-colors',
        active ? 'bg-primary-container text-on-primary-container' : 'text-on-surface-variant hover:bg-surface-variant')}
      aria-pressed={active}
    >
      <Icon className="h-3.5 w-3.5" /> <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function SaveIndicator({ state, canEdit }: { state: SaveState; canEdit: boolean }) {
  if (!canEdit) return <span className="inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Read-only</span>;
  if (state === 'saving')
    return <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</span>;
  if (state === 'saved')
    return <span className="inline-flex items-center gap-1.5 text-success"><Check className="h-3.5 w-3.5" /> Saved</span>;
  if (state === 'error')
    return <span className="inline-flex items-center gap-1.5 text-danger"><CloudOff className="h-3.5 w-3.5" /> Save failed</span>;
  return <span className="inline-flex items-center gap-1.5"><Check className="h-3.5 w-3.5 opacity-40" /> All changes saved</span>;
}
